/**
 * W98 (V6.0) — Die Heute-Inbox ganz oben auf dem Dashboard.
 *
 * Beantwortet EINE Frage: „Muss ich heute handeln — und wenn ja, was?"
 *  - Markt-Ampel-Leiste: 6 Chips (Phase · Makro · Stimmung · VIX ·
 *    Kapitulation · Krypto) ersetzen 8 Markt-Karten als Erstinformation;
 *    die Detail-Panels liegen einen Klick tiefer („Markt-Details").
 *  - Max. 3 Entscheidungs-Karten, DB-first aus dem 18:15-Cron
 *    (Krypto-Boden, Reversal-Radar, Kapitulation) + Plan-/Positions-
 *    Zustand. „Heute nichts zu tun" ist ein bewusster Zustand.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  buildInboxCards,
  buildMarktChips,
  type InboxInput,
  type MarktChip,
} from '@/lib/heute-inbox';
import { insertJournalEntry } from '@/lib/queries';
import { fetchCryptoBodenAll } from '@/lib/crypto-boden-read';
import { fetchReversalRadarFromDb } from '@/lib/reversal-radar';
import {
  fetchLatestInternals,
} from '@/components/dashboard/CapitulationRadarCard';
import { fetchLatestMacroRegime } from '@/components/dashboard/MacroRegimePanel';
import { fetchLatestSentiment } from '@/components/dashboard/SentimentRegimePanel';
import { StatusDot } from '@/components/ui/StatusDot';
import { useDataState } from '@/hooks/use-data-state';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { cn } from '@/lib/utils';

const CHIP_TONE: Record<MarktChip['tone'], string> = {
  green: 'border-emerald-800/60 bg-emerald-950/30 text-emerald-200',
  yellow: 'border-amber-800/60 bg-amber-950/30 text-amber-200',
  red: 'border-red-800/60 bg-red-950/30 text-red-200',
  gray: 'border-cockpit-border bg-cockpit-surface2 text-zinc-400',
};

const CARD_TONE: Record<string, string> = {
  green: 'border-emerald-800/60 bg-emerald-950/20',
  yellow: 'border-amber-800/60 bg-amber-950/20',
  red: 'border-red-800/60 bg-red-950/20',
  gray: 'border-cockpit-border bg-cockpit-surface2',
};

/** W100(F) — die vier ehrlichen Gründe, heute NICHT zu handeln. */
const NO_TRADE_REASONS = [
  'Markt passt nicht',
  'Schon genug Risiko offen',
  'These überzeugt mich nicht',
  'Keine Ruhe heute',
] as const;

function dismissedStorageKey(): string {
  return `inbox.heute-nicht.${new Date().toISOString().slice(0, 10)}`;
}

function loadDismissed(): Set<string> {
  try {
    const raw = window.localStorage.getItem(dismissedStorageKey());
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

export function HeuteInbox({
  phaseAggregate,
  vixLive,
  cryptoSupportive,
  stopRisk,
}: {
  phaseAggregate: 'bull' | 'korrektur' | 'bear' | 'accumulation' | 'neutral' | null;
  vixLive: number | null;
  cryptoSupportive: boolean | null;
  stopRisk: InboxInput['stopRisk'];
}) {
  const navigate = useNavigate();
  const { isDemoMode, linkTo } = useDemoMode();

  const internals = useDataState({
    queryKey: ['market-internals-latest'],
    queryFn: (signal) => fetchLatestInternals(signal),
    ttlSeconds: 30 * 60,
    isEmpty: (r) => r === null,
  });
  const macro = useDataState({
    queryKey: ['macro-regime', 'latest'],
    queryFn: () => fetchLatestMacroRegime(),
    ttlSeconds: 60 * 60,
    isEmpty: (r) => r === null,
  });
  const sentiment = useDataState({
    queryKey: ['sentiment-regime', 'latest'],
    queryFn: () => fetchLatestSentiment(),
    ttlSeconds: 60 * 60,
    isEmpty: (r) => r === null,
  });
  const radar = useDataState({
    queryKey: ['reversal-radar-db'],
    queryFn: (signal) => fetchReversalRadarFromDb(signal),
    ttlSeconds: 30 * 60,
    isEmpty: (r) => !r || r.length === 0,
  });
  const boden = useDataState({
    queryKey: ['crypto-boden-all'],
    queryFn: (signal) => fetchCryptoBodenAll(signal),
    ttlSeconds: 30 * 60,
    isEmpty: (m) => !m || m.size === 0,
  });

  const intern = internals.state.data ?? null;
  const chips = buildMarktChips({
    phaseAggregate,
    vix: intern?.vix ?? vixLive,
    macroRegime: macro.state.data?.regime ?? null,
    sentimentSignal: sentiment.state.data?.regime_signal ?? null,
    sentimentFearGreed: sentiment.state.data?.fg_index ?? null,
    capitulationScore: intern?.capitulation_score ?? null,
    capitulationFlag: intern?.capitulation_flag ?? null,
    thrustState: intern?.thrust_state ?? null,
    cryptoSupportive,
  });

  const bodenReady = Array.from(boden.state.data?.values() ?? [])
    .filter((r) => r.t1_ready === true)
    .map((r) => ({ symbol: r.symbol, bodenScore: r.boden_score }));
  const stabilizing = (radar.state.data ?? [])
    .filter((r) => r.stabilizing)
    .map((r) => ({ symbol: r.symbol, layer: r.layer, score: r.score }));

  const { cards, overflow } = buildInboxCards({
    stopRisk,
    bodenReady,
    stabilizing,
    capitulationFlag: intern?.capitulation_flag ?? null,
    thrustState: intern?.thrust_state ?? null,
  });

  // W100(F) — „Heute nicht": Karte für heute ausblenden + Grund ins Journal.
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed());
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [savingNo, setSavingNo] = useState(false);
  const visibleCards = cards.filter((c) => !dismissed.has(c.key));

  const handleHeuteNicht = async (
    card: (typeof cards)[number],
    reason: (typeof NO_TRADE_REASONS)[number]
  ) => {
    setSavingNo(true);
    try {
      await insertJournalEntry({
        position_id: null,
        trade_plan_id: null,
        date: new Date().toISOString().slice(0, 10),
        action_type: 'observation',
        title: `Heute nicht: ${card.title}`,
        body_markdown: `Entscheidungs-Karte bewusst übersprungen.\n\n**Karte:** ${card.satz}\n**Grund:** ${reason}`,
        setup_type: null,
        market_phase: 'transition',
        rule_followed: true,
        discipline_score: 8,
        emotional_state: null,
        error_tags: [],
        positive_tags: [],
        plan_vs_execution: null,
        attached_chart_url: null,
      });
    } catch {
      // Journal-Insert ist nice-to-have — das Ausblenden passiert trotzdem,
      // sonst klemmt die Inbox an einem RLS-/Netz-Fehler.
    }
    const next = new Set(dismissed);
    next.add(card.key);
    setDismissed(next);
    setPickerFor(null);
    setSavingNo(false);
    try {
      window.localStorage.setItem(dismissedStorageKey(), JSON.stringify([...next]));
    } catch {
      // localStorage voll/blockiert — nicht fatal
    }
  };

  const openMarktDetails = () => {
    const el = document.getElementById('markt-details');
    if (el instanceof HTMLDetailsElement) {
      el.open = true;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Audit 13.07. — Karten mit '#anker'-Route öffnen ein Aufklapp-Panel auf
  // DIESER Seite (z. B. Markt-Details/Kapitulations-Radar) statt zu navigieren.
  const goCard = (route: string) => {
    if (route.startsWith('#')) {
      const el = document.getElementById(route.slice(1));
      if (el instanceof HTMLDetailsElement) {
        el.open = true;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    navigate(linkTo(route));
  };

  return (
    <section className="space-y-3">
      {/* Markt-Ampel-Leiste — Klick öffnet die Detail-Panels */}
      <div className="flex flex-wrap items-center gap-1.5">
        {chips.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={openMarktDetails}
            title={c.hint}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[11px] font-medium transition-colors hover:brightness-110',
              CHIP_TONE[c.tone]
            )}
          >
            <StatusDot tone={c.tone} size="sm" />
            <span className="opacity-75">{c.label}</span>
            <span className="font-semibold">{c.value}</span>
          </button>
        ))}
        <button
          type="button"
          onClick={openMarktDetails}
          className="text-[11px] text-zinc-500 underline underline-offset-2 hover:text-zinc-300 ml-1"
        >
          Markt-Details
        </button>
      </div>

      {/* Entscheidungs-Karten ODER „Heute nichts zu tun" */}
      {cards.length === 0 ? (
        <div className="rounded-lg border border-cockpit-border bg-cockpit-surface p-4 text-sm text-zinc-300 flex items-center gap-3">
          <span className="text-xl" aria-hidden>
            ☕
          </span>
          <div>
            <div className="font-semibold text-zinc-200">Heute nichts zu tun.</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              Kein Kauf-Fenster, kein Stop in Gefahr, kein Markt-Ereignis. Das System
              meldet sich per Mail, sobald sich das ändert.
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {visibleCards.map((card) => (
            <div
              key={card.key}
              className={cn(
                'rounded-lg border p-3 transition-colors',
                CARD_TONE[card.tone]
              )}
            >
              <button
                type="button"
                onClick={() => goCard(card.route)}
                className="block w-full text-left hover:brightness-110"
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
                  <span aria-hidden>{card.emoji}</span>
                  <span className="min-w-0 truncate">{card.title}</span>
                </div>
                <p className="text-xs text-zinc-300 mt-1.5 leading-snug">{card.satz}</p>
              </button>
              <div className="flex items-center justify-between mt-2 gap-2">
                <button
                  type="button"
                  onClick={() => goCard(card.route)}
                  className="text-[11px] font-medium underline underline-offset-2 opacity-90 hover:opacity-100"
                >
                  {card.aktion} →
                </button>
                {/* W100(F) — bewusstes NEIN ist eine Entscheidung und geht
                    ins Journal: das System lernt auch aus Nicht-Trades. */}
                {!isDemoMode && (
                  <button
                    type="button"
                    onClick={() => setPickerFor(pickerFor === card.key ? null : card.key)}
                    className="text-[10px] text-zinc-500 hover:text-zinc-300 underline underline-offset-2"
                  >
                    Heute nicht
                  </button>
                )}
              </div>
              {pickerFor === card.key && (
                <div className="mt-2 pt-2 border-t border-zinc-700/40 flex flex-wrap gap-1.5">
                  {NO_TRADE_REASONS.map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      disabled={savingNo}
                      onClick={() => handleHeuteNicht(card, reason)}
                      className="rounded-full border border-zinc-700/60 bg-zinc-900/40 px-2 py-0.5 text-[10px] text-zinc-300 hover:bg-zinc-800/60 disabled:opacity-50"
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => navigate(linkTo('/watchlist'))}
          className="text-[11px] text-zinc-500 underline underline-offset-2 hover:text-zinc-300"
        >
          +{overflow} weitere{overflow === 1 ? 's' : ''} Ereignis{overflow === 1 ? '' : 'se'} — alle
          in der Watchlist (Radar „Jetzt") ansehen →
        </button>
      )}
      {isDemoMode && stopRisk.length === 0 && (
        <div className="text-[10px] text-zinc-600">
          Demo-Modus: Positions- und Plan-Ereignisse erscheinen nur eingeloggt.
        </div>
      )}
    </section>
  );
}
